import { useEffect, useState, type FormEvent } from "react";
import { ProductCard } from "../../components/Cards";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest } from "../../lib/api";
import { useAdminProducts, useInvalidateAll } from "../../lib/queries";
import type { Product } from "../../types";
import { AdminPhotoField, Feedback, Field, useAction } from "./shared";
import { PRODUCT_ICON_CHOICES } from "../../lib/product-icons";

const ICONS: readonly string[] = PRODUCT_ICON_CHOICES;
const TINTS = ["mediterraneo", "caribe", "maldivas", "waikiki"];

export function AdminProductsPage() {
  usePageHeader("Productos", "Las tres tarjetas de “Finanzas”: copy, tasa, foto y visibilidad.");
  const products = useAdminProducts();
  if (products.isLoading) return <div className="page-state"><Spinner label="Cargando productos" /></div>;
  return (
    <div className="page-container">
      <div className="admin-stack">
        {(products.data?.products ?? []).map((product) => <ProductEditor key={product.id} product={product} />)}
      </div>
    </div>
  );
}

function ProductEditor({ product }: { product: Product }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [form, setForm] = useState(product);
  useEffect(() => setForm(product), [product]);
  const set = <K extends keyof Product>(key: K, value: Product[K]) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await action.run("save", async () => {
      await apiRequest(`/api/v1/admin/products/${product.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          eyebrow: form.eyebrow, title: form.title, tag: form.tag, rateLabel: form.rateLabel, rateSuffix: form.rateSuffix,
          badge: form.badge || null, icon: form.icon, tint: form.tint, photoId: form.photoId, enabled: form.enabled, sortOrder: form.sortOrder
        })
      });
      await invalidate();
      return "Producto actualizado.";
    });
  };
  return (
    <section className="card admin-two-col product-editor">
      <form className="admin-form" onSubmit={submit}>
        <div className="section-heading"><div><span className="page-eyebrow">{product.id}</span><h2>{product.title}</h2></div><span className={`tag ${form.enabled ? "active" : "suspended"}`}>{form.enabled ? "visible" : "oculto"}</span></div>
        <div className="field-grid">
          <Field label="Etiqueta superior" htmlFor={`p-eyebrow-${product.id}`}><input id={`p-eyebrow-${product.id}`} className="standalone-input" value={form.eyebrow} onChange={(event) => set("eyebrow", event.target.value)} maxLength={60} required /></Field>
          <Field label="Título" htmlFor={`p-title-${product.id}`}><input id={`p-title-${product.id}`} className="standalone-input" value={form.title} onChange={(event) => set("title", event.target.value)} maxLength={80} required /></Field>
        </div>
        <Field label="Descripción (un salto de línea = párrafo aparte)" htmlFor={`p-tag-${product.id}`}><textarea id={`p-tag-${product.id}`} className="standalone-input textarea" rows={2} value={form.tag} onChange={(event) => set("tag", event.target.value)} maxLength={240} required /></Field>
        <div className="field-grid three">
          <Field label="Tasa" htmlFor={`p-rate-${product.id}`}><input id={`p-rate-${product.id}`} className="standalone-input" value={form.rateLabel} onChange={(event) => set("rateLabel", event.target.value)} maxLength={20} required /></Field>
          <Field label="Sufijo de tasa" htmlFor={`p-suffix-${product.id}`}><input id={`p-suffix-${product.id}`} className="standalone-input" value={form.rateSuffix} onChange={(event) => set("rateSuffix", event.target.value)} maxLength={20} required /></Field>
          <Field label="Insignia" htmlFor={`p-badge-${product.id}`}><input id={`p-badge-${product.id}`} className="standalone-input" value={form.badge ?? ""} onChange={(event) => set("badge", event.target.value)} maxLength={40} placeholder="Seguro incluido" /></Field>
        </div>
        <div className="field-grid three">
          <Field label="Ícono" htmlFor={`p-icon-${product.id}`}>
            <select id={`p-icon-${product.id}`} className="standalone-input" value={form.icon} onChange={(event) => set("icon", event.target.value)}>{ICONS.map((icon) => <option key={icon} value={icon}>{icon}</option>)}</select>
          </Field>
          <Field label="Tono" htmlFor={`p-tint-${product.id}`}>
            <select id={`p-tint-${product.id}`} className="standalone-input" value={form.tint} onChange={(event) => set("tint", event.target.value)}>{TINTS.map((tint) => <option key={tint} value={tint}>{tint}</option>)}</select>
          </Field>
          <Field label="Orden" htmlFor={`p-order-${product.id}`}><input id={`p-order-${product.id}`} className="standalone-input" inputMode="numeric" value={String(form.sortOrder)} onChange={(event) => set("sortOrder", Number(event.target.value.replace(/\D/g, "")) || 0)} /></Field>
        </div>
        <AdminPhotoField kind="product" value={form.photoId} onChange={(id) => set("photoId", id)} label="Foto de la tarjeta" />
        <label className="check-row inline"><input type="checkbox" checked={form.enabled} onChange={(event) => set("enabled", event.target.checked)} /><span>Visible en Finanzas</span></label>
        <Feedback error={action.error} notice={action.notice} />
        <button className="primary-button solid" type="submit" disabled={action.busy !== null}>{action.busy === "save" ? <Spinner /> : null} Guardar producto</button>
      </form>
      <div className="product-preview">
        <span className="rail-title">Vista previa</span>
        <ProductCard product={{ ...form, photoUrl: form.photoId ? `/api/v1/media/${form.photoId}` : null }} onClick={() => undefined} />
      </div>
    </section>
  );
}
